import type { Station } from './types';

/** Interpolate the uniform physics station domain at arbitrary arc length. */
export function stationAtS(stations: readonly Station[], s: number): Station | null {
  const first = stations[0];
  const last = stations[stations.length - 1];
  if (!first || !last || last.s <= first.s) return null;
  if (s <= first.s) return first;
  if (s >= last.s) return last;
  const step = (last.s - first.s) / (stations.length - 1);
  const i = Math.min(stations.length - 2, Math.floor((s - first.s) / step));
  const a = stations[i]!;
  const b = stations[i + 1]!;
  const t = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0;
  const lerp = (p: number, q: number) => p + (q - p) * t;
  return {
    s,
    x: lerp(a.x, b.x),
    y: lerp(a.y, b.y),
    z: lerp(a.z, b.z),
    tx: lerp(a.tx, b.tx),
    tz: lerp(a.tz, b.tz),
    nx: lerp(a.nx, b.nx),
    nz: lerp(a.nz, b.nz),
    grade: lerp(a.grade, b.grade),
    legIndex: t < 0.5 ? a.legIndex : b.legIndex,
    radius:
      Number.isFinite(a.radius) && Number.isFinite(b.radius)
        ? lerp(a.radius, b.radius)
        : Math.min(a.radius, b.radius),
  };
}

export function nearestStationIndex(
  stations: readonly Station[],
  x: number,
  z: number,
  hint?: number,
): number {
  if (stations.length === 0) return -1;
  const from = hint === undefined ? 0 : Math.max(0, hint - 3);
  const to = hint === undefined ? stations.length - 1 : Math.min(stations.length - 1, hint + 3);
  let best = from;
  let bestD = Infinity;
  for (let i = from; i <= to; i++) {
    const st = stations[i]!;
    const d = (st.x - x) ** 2 + (st.z - z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
